import { Dialog, makeStyles, Typography } from "@material-ui/core"
import React, { FC, useState } from "react"
import { InviteCodeFragment } from "../../generated/graphql"
import { useInviteCode } from "../../hooks/useInviteCode"
import { useUser } from "../../hooks/useUser"
import { useApi } from "../../utils/API"
import { colors } from "../../utils/theme"
import { PrimaryButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import Input from "../atoms/input/Input"

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  dialogInput: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  dialogButton: {
    width: "100%",
  },
}))

interface Props {
  onSuccess: (code: InviteCodeFragment) => void
  onCancel: () => void
}

const AddClient: FC<Props> = ({ onSuccess, onCancel }) => {
  const classes = useStyles()
  const [code, setCode] = useState<string>("")
  const [codeError, setCodeError] = useState<boolean>(false)
  const [codeErrorMessage, setCodeErrorMessage] = useState<string>("")
  const { addClient, checkIfClientAssigned } = useInviteCode()
  const { user } = useUser()
  const API = useApi()

  return (
    <div className={classes.dialogContent}>
      <div className={classes.dialogCloseContainer}>
        <button className={classes.closeButton} onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      <Typography variant="h2" className={classes.dialogTitle}>
        Add New Client
      </Typography>
      <Typography variant="body1" className={classes.dialogSubtitle}>
        Ask your client to share their unique code when they add a practitioner
        from their account page.
      </Typography>
      <Typography variant="body1" className={classes.dialogSubtitle}>
        Once they confirm you will be able to access their data and provide
        personalized support.
      </Typography>
      <Input
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        label="Code"
        placeholder="Code"
        className={classes.dialogInput}
        error={codeError}
        helperText={codeErrorMessage}
      />
      <PrimaryButton
        onClick={async () => {
          setCodeError(false)
          setCodeErrorMessage("")
          if (!code || (code && code.length !== 7)) {
            setCodeError(true)
            setCodeErrorMessage(
              "You need to enter a code to continue. " +
                "It must be seven characters including numbers and letters only."
            )
            return
          } else {
            if (!user) {
              throw new Error(
                "Portal User not found. You probably shouldn't be logged in..."
              )
            }
            const codeState = await checkIfClientAssigned(
              code.toUpperCase(),
              user.id
            )
            if (codeState === "assigned") {
              setCodeError(true)
              setCodeErrorMessage(
                "The client associated with this code has already been added."
              )
              return
            } else if (codeState === "invalid") {
              setCodeError(true)
              setCodeErrorMessage(
                "The code you entered was not found. Verify code provided by client."
              )
              return
            }
            const result = await addClient(code.toUpperCase(), user.id)
            if (!result) {
              setCodeError(true)
              setCodeErrorMessage(
                "This code is incorrect. It must be seven characters including " +
                  "numbers and letters only. Verify code provided by client."
              )
              return
            }
            API.scheduleConnectPractitionerNotification(code.toUpperCase())
            setCode("")
            onSuccess(result)
          }
        }}
        className={classes.dialogButton}
        type="submit"
      >
        Add Client
      </PrimaryButton>
    </div>
  )
}

const Success: FC<Props & { inviteCodeUserFullName: string }> = ({
  onCancel,
  inviteCodeUserFullName,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.dialogContent}>
      <div className={classes.dialogCloseContainer}>
        <button className={classes.closeButton} onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      <Typography variant="h2" component="h1" className={classes.dialogTitle}>
        Client Successfully Added
      </Typography>
      <Typography variant="body1" className={classes.dialogSubtitle}>
        {`Your client ${inviteCodeUserFullName} has been successfully added to your dashboard. ` +
          "Once they confirm, you will be able to access their data " +
          "and provide personalized support."}
      </Typography>
      <PrimaryButton
        onClick={onCancel}
        className={classes.dialogButton}
        type="submit"
      >
        Close
      </PrimaryButton>
    </div>
  )
}
const AddClientDialog: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const classes = useStyles()
  const [inviteCode, setInviteCode] = useState<InviteCodeFragment | undefined>()

  const close = () => {
    setInviteCode(undefined)
    onClose()
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="xs"
      open={open}
      onClose={close}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      {!inviteCode ? (
        <AddClient onSuccess={setInviteCode} onCancel={onClose} />
      ) : (
        <Success
          onCancel={close}
          inviteCodeUserFullName={
            `${inviteCode!.user.first_name!} ` +
            `${inviteCode!.user.last_name!}`
          }
          onSuccess={close}
        />
      )}
    </Dialog>
  )
}

export default AddClientDialog
